import classNames from "classnames";
import { DateTime } from "luxon";
import { useCallback, useState, type MouseEvent } from "react";
import EmptyState from "@/components/EmptyState";
import IconButton from "@/components/IconButton";
import { MaterialNames } from "@/components/material_symbol_names";
import { TableCard, RowLink, TableCardRow } from "@/components/Table";
import { useRecentlyVisited, type RecentlyVisitedItem } from "@/hooks/useRecentlyVisited";
import type { Path } from "@/router";
import { useNavigate } from "@/router";
import DashboardRowDetails from "./DashboardRowDetails";
import DashboardTableSkeleton from "./DashboardTableSkeleton";
import { useIsDashboardStackedTableRows } from "../_hooks/useIsDashboardSingleColumn";
import {
  DASHBOARD_PANEL_SHELL_CLASS,
  DASHBOARD_TABLE_CONTENT_CELL,
  DASHBOARD_TABLE_META_CELL
} from "../_utils/dashboardPanelStyles";
import { DASHBOARD_TABLE_CARD_BODY_CLASS, getDashboardPreviewTableClassName } from "../_utils/dashboardPreviewTable";

const COLUMN_COUNT = 2;
const INITIAL_VISIBLE_COUNT = 3;
const LOAD_MORE_INCREMENT = 4;

function RecentItemDetails({ item }: { item: RecentlyVisitedItem }) {
  const isPerson = item.url.startsWith("/people/");

  return (
    <DashboardRowDetails
      picture={isPerson ? item.image : undefined}
      icon={!isPerson ? (item.image as MaterialNames) : undefined}
      title={item.text}
      text={item.subtext}
    />
  );
}

function VisitedAtLabel({ dateTime, className }: { dateTime: RecentlyVisitedItem["dateTime"]; className?: string }) {
  const parsed = DateTime.fromISO(dateTime);
  const visitedAt = parsed.isValid ? parsed : DateTime.fromJSDate(new Date(dateTime));

  return (
    <span className={classNames("text-body-small text-sys-brand-on-surface-variant whitespace-nowrap", className)}>
      {visitedAt.toRelative({ base: DateTime.now() })}
    </span>
  );
}

function RemoveRecentlyVisitedButton({
  item,
  onRemove,
  revealOnHover = true
}: {
  item: RecentlyVisitedItem;
  onRemove: (item: RecentlyVisitedItem) => void;
  revealOnHover?: boolean;
}) {
  return (
    <IconButton
      name="close"
      skipMargin
      ariaLabel={`Remove ${item.text} from recently visited`}
      data-testid={`recently-visited-remove-${item.url}`}
      className={classNames(
        "!m-0 transition-opacity",
        revealOnHover &&
          "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100"
      )}
      containerClassName="shrink-0"
      iconClassName="text-sys-brand-on-surface-variant"
      onClick={(event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onRemove(item);
      }}
    />
  );
}

function RecentlyVisitedRowMeta({
  item,
  onRemove,
  revealRemoveOnHover
}: {
  item: RecentlyVisitedItem;
  onRemove: (item: RecentlyVisitedItem) => void;
  revealRemoveOnHover: boolean;
}) {
  return (
    <div className="relative flex items-center justify-end h-10 w-full min-w-[5.5rem]">
      <VisitedAtLabel
        dateTime={item.dateTime}
        className={classNames(
          revealRemoveOnHover && "transition-opacity group-hover:opacity-0 group-focus-within:opacity-0"
        )}
      />
      <div
        className={classNames(
          "absolute inset-0 flex items-center justify-end",
          revealRemoveOnHover &&
            "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100"
        )}
      >
        <RemoveRecentlyVisitedButton item={item} onRemove={onRemove} revealOnHover={false} />
      </div>
    </div>
  );
}

function RecentlyVisitedRow({
  item,
  onRemove
}: {
  item: RecentlyVisitedItem;
  onRemove: (item: RecentlyVisitedItem) => void;
}) {
  const navigate = useNavigate();
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const openItem = () => navigate(item.url as Path);

  return (
    <TableCardRow
      columnCount={COLUMN_COUNT}
      rowTestId={`recently-visited-row-${item.url}`}
      onClick={openItem}
      asButton={false}
      ariaLabel={`Open recently visited: ${item.text}`}
      stackedLayout={isStackedTableRows}
      className="group"
      mobile={
        <div className="flex min-w-0 items-start gap-2">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <RecentItemDetails item={item} />
            <VisitedAtLabel dateTime={item.dateTime} />
          </div>
          <RemoveRecentlyVisitedButton item={item} onRemove={onRemove} revealOnHover={false} />
        </div>
      }
    >
      <td className={classNames(DASHBOARD_TABLE_CONTENT_CELL, "max-w-0")}>
        <RecentItemDetails item={item} />
      </td>
      <td className={DASHBOARD_TABLE_META_CELL}>
        <RecentlyVisitedRowMeta item={item} onRemove={onRemove} revealRemoveOnHover={true} />
      </td>
    </TableCardRow>
  );
}

export default function RecentlyVisitedTable() {
  const { getRecentlyVisitedItems, removeRecentlyVisitedItem, loading, error } = useRecentlyVisited();
  const isStackedTableRows = useIsDashboardStackedTableRows();
  const items = getRecentlyVisitedItems();
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const visibleItems = items.slice(0, visibleCount);
  const hasMore = visibleCount < items.length;
  const isFullyExpanded = items.length > INITIAL_VISIBLE_COUNT && visibleCount >= items.length;
  const showFooter = hasMore || isFullyExpanded;
  const isEmpty = !loading && !error && items.length === 0;

  const handleRemove = useCallback(
    (item: RecentlyVisitedItem) => {
      removeRecentlyVisitedItem({ type: item.type, entityId: item.entityId });
      setVisibleCount((count) => Math.min(count, Math.max(items.length - 1, INITIAL_VISIBLE_COUNT)));
    },
    [items.length, removeRecentlyVisitedItem]
  );

  return (
    <TableCard
      title="Recently Visited"
      testId="home-recently-visited-card"
      bodyClassName={DASHBOARD_TABLE_CARD_BODY_CLASS}
      shellClassName={DASHBOARD_PANEL_SHELL_CLASS}
    >
      <table
        className={getDashboardPreviewTableClassName(isStackedTableRows)}
        data-testid="home-recently-visited-table"
      >
        {isStackedTableRows ? null : (
          <colgroup>
            <col />
            <col className="w-28" />
          </colgroup>
        )}
        <tbody>
          {loading ? (
            <DashboardTableSkeleton columnCount={COLUMN_COUNT} />
          ) : error ? (
            <tr data-testid="home-recently-visited-error-row">
              <td colSpan={COLUMN_COUNT} className="px-4 py-6 text-center text-body-medium text-sys-brand-error">
                Couldn't load recently visited. Try refreshing the page.
              </td>
            </tr>
          ) : isEmpty ? (
            <tr>
              <td colSpan={COLUMN_COUNT}>
                <EmptyState iconName="history" title="No Recently Visited Items" />
              </td>
            </tr>
          ) : (
            <>
              {visibleItems.map((item) => (
                <RecentlyVisitedRow key={`${item.type}:${item.entityId}`} item={item} onRemove={handleRemove} />
              ))}
              {showFooter ? (
                <RowLink
                  columnCount={COLUMN_COUNT}
                  onClick={() =>
                    isFullyExpanded
                      ? setVisibleCount(INITIAL_VISIBLE_COUNT)
                      : setVisibleCount((n) => Math.min(n + LOAD_MORE_INCREMENT, items.length))
                  }
                  testId={isFullyExpanded ? "recently-visited-collapse" : "recently-visited-load-more"}
                >
                  {isFullyExpanded ? "Collapse" : "Load More"}
                </RowLink>
              ) : null}
            </>
          )}
        </tbody>
      </table>
    </TableCard>
  );
}
